import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Calendar, BookOpen, Loader2, ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedJournalPage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const fetchShared = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/journal/shared/${shareId}`);
      if (res.data.success) setData(res.data);
      else setError('Not found');
    } catch {
      setError('This shared journal is no longer available.');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    fetchShared();
  }, [fetchShared]);

  const getPhotoUrl = (photo) => `${API}/api/journal/shared-file/${shareId}/${photo.storage_path}`;

  const photos = data?.photos || [];
  const share = data?.share || {};

  const openLightbox = (index) => setLightbox(index);
  const navLightbox = (dir) => {
    if (lightbox === null) return;
    setLightbox((lightbox + dir + photos.length) % photos.length);
  };

  // Group photos by day
  const photosByDay = {};
  photos.forEach(p => {
    const day = p.day_number || 1;
    if (!photosByDay[day]) photosByDay[day] = [];
    photosByDay[day].push(p);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-gold" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70 flex items-center justify-center p-6">
        <Card className="bg-white/95 backdrop-blur-xl max-w-md w-full shadow-xl">
          <CardContent className="py-12 text-center">
            <Camera className="w-12 h-12 text-ocean/20 mx-auto mb-4" />
            <h2 className="font-serif text-xl text-ocean font-bold mb-2">Journal Not Found</h2>
            <p className="text-charcoal/50 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />

      {/* Header band */}
      <div className="bg-ocean-dark/50 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <img src="/logo.jpg" alt="Triple A" className="w-9 h-9 rounded-lg object-contain" />
          <div>
            <p className="text-white/50 text-xs font-medium tracking-wide uppercase">Shared Photo Journal</p>
            <h1 className="font-serif text-xl sm:text-2xl text-white font-bold leading-tight">
              {share.destination_city}{share.destination_country ? `, ${share.destination_country}` : ''}
            </h1>
          </div>
        </div>
      </div>

      <main className="py-10 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          {/* Trip info banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-4 text-white/60 text-sm"
          >
            {share.start_date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> {share.start_date}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4" /> {share.photo_count} photo{share.photo_count !== 1 ? 's' : ''}
            </span>
            {share.destination_country && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" /> {share.destination_country}
              </span>
            )}
          </motion.div>

          {/* Photos grouped by day */}
          {Object.entries(photosByDay)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([dayNum, dayPhotos]) => (
              <motion.div
                key={dayNum}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs font-bold">{dayNum}</span>
                  Day {dayNum}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {dayPhotos.map((photo, i) => {
                    const globalIdx = photos.findIndex(p => p.id === photo.id);
                    return (
                      <div
                        key={photo.id}
                        onClick={() => openLightbox(globalIdx)}
                        className="relative group rounded-xl overflow-hidden border border-white/10 bg-white/5 cursor-pointer aspect-square"
                        data-testid={`shared-photo-${photo.id}`}
                      >
                        <img
                          src={getPhotoUrl(photo)}
                          alt={photo.caption || ''}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                            <p className="text-white text-xs truncate">{photo.caption}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}

          {/* Footer branding */}
          <div className="mt-12 text-center border-t border-white/10 pt-6">
            <p className="text-white/30 text-xs">
              Shared via <span className="text-gold/60 font-semibold">Triple A Travel Solutions</span>
            </p>
          </div>
        </div>
      </main>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="shared-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); navLightbox(-1); }} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); navLightbox(1); }} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="max-w-3xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img
              src={getPhotoUrl(photos[lightbox])}
              alt={photos[lightbox]?.caption || ''}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            {photos[lightbox]?.caption && (
              <p className="text-white/80 text-sm mt-4">{photos[lightbox].caption}</p>
            )}
            <p className="text-white/40 text-xs mt-1">{lightbox + 1} / {photos.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}
